import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Mail, Phone, X, Edit2, Trash2, 
  UserPlus, Send, Check, AlertCircle, Shield,
  Eye, Vote, FileText, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SousComptes = () => {
  const { user, token } = useAuth();
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    type: 'delegue',
    permissions: {
      can_view_ag: true,
      can_vote: false,
      can_view_documents: true,
      can_view_incidents: false,
      can_create_incidents: false
    }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSubAccounts();
  }, [token]);

  const loadSubAccounts = async () => {
    try {
      const response = await axios.get(`${API}/sub-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubAccounts(response.data);
    } catch (err) {
      console.error('Error loading sub-accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingAccount) {
        await axios.put(`${API}/sub-accounts/${editingAccount.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/sub-accounts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      loadSubAccounts();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce sous-compte ?')) return;
    
    try {
      await axios.delete(`${API}/sub-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSubAccounts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleResendInvitation = async (accountId) => {
    try {
      await axios.post(`${API}/sub-accounts/${accountId}/resend-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Invitation renvoyée avec succès');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const openCreateModal = () => {
    setEditingAccount(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      type: 'delegue',
      permissions: {
        can_view_ag: true,
        can_vote: false,
        can_view_documents: true,
        can_view_incidents: false,
        can_create_incidents: false
      }
    });
    setShowModal(true);
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      nom: account.nom,
      prenom: account.prenom,
      email: account.email,
      telephone: account.telephone || '',
      type: account.type,
      permissions: account.permissions || {}
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccount(null);
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      permissions: {
        can_view_ag: true,
        can_vote: type === 'delegue',
        can_view_documents: true,
        can_view_incidents: type === 'locataire',
        can_create_incidents: type === 'locataire'
      }
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'actif':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Actif</span>;
      case 'invitation_envoyee':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Invitation envoyée</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'delegue':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center space-x-1"><Vote size={12} /><span>Délégué</span></span>;
      case 'locataire':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center space-x-1"><Users size={12} /><span>Locataire</span></span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mes Sous-Comptes</h1>
          <p className="text-gray-600">Gérez les accès pour vos délégués et locataires</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2.5 px-5 rounded-full flex items-center space-x-2 transition-colors"
          data-testid="add-sub-account-btn"
        >
          <UserPlus size={20} />
          <span>Nouveau sous-compte</span>
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Types de sous-comptes</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li><strong>Délégué :</strong> Peut assister aux AG et voter en votre nom (si vous lui donnez procuration)</li>
              <li><strong>Locataire :</strong> Peut consulter les documents et déclarer des incidents liés au logement</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sub-accounts List */}
      {subAccounts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Users className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucun sous-compte</h3>
          <p className="text-gray-500 mb-6">Créez un sous-compte pour un délégué ou un locataire</p>
          <button
            onClick={openCreateModal}
            className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-full inline-flex items-center space-x-2"
          >
            <Plus size={18} />
            <span>Créer un sous-compte</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subAccounts.map((account) => (
            <div 
              key={account.id} 
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
              data-testid={`sub-account-${account.id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00D3A8] to-[#00C7A1] rounded-full flex items-center justify-center text-white font-semibold">
                    {account.prenom?.[0]}{account.nom?.[0]}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{account.prenom} {account.nom}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      {getTypeBadge(account.type)}
                      {getStatusBadge(account.status)}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEditModal(account)}
                    className="p-1.5 text-gray-400 hover:text-[#00D3A8] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(account.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail size={14} className="mr-2 text-gray-400" />
                  <span className="truncate">{account.email}</span>
                </div>
                {account.telephone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone size={14} className="mr-2 text-gray-400" />
                    <span>{account.telephone}</span>
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs text-gray-500 mb-2">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {account.permissions?.can_view_ag && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">AG</span>
                  )}
                  {account.permissions?.can_vote && (
                    <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Vote</span>
                  )}
                  {account.permissions?.can_view_documents && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Documents</span>
                  )}
                  {account.permissions?.can_view_incidents && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Incidents</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              {account.status === 'invitation_envoyee' && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleResendInvitation(account.id)}
                    className="w-full text-sm text-[#00D3A8] hover:text-[#00C7A1] flex items-center justify-center space-x-1"
                  >
                    <Send size={14} />
                    <span>Renvoyer l&apos;invitation</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editingAccount ? 'Modifier le sous-compte' : 'Nouveau sous-compte'}
                </h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de sous-compte</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleTypeChange('delegue')}
                    className={`p-4 border-2 rounded-xl text-left transition-colors ${
                      formData.type === 'delegue' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Vote className={`mb-2 ${formData.type === 'delegue' ? 'text-purple-600' : 'text-gray-400'}`} size={24} />
                    <p className="font-semibold text-gray-800">Délégué</p>
                    <p className="text-xs text-gray-500">Peut voter aux AG</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTypeChange('locataire')}
                    className={`p-4 border-2 rounded-xl text-left transition-colors ${
                      formData.type === 'locataire' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className={`mb-2 ${formData.type === 'locataire' ? 'text-blue-600' : 'text-gray-400'}`} size={24} />
                    <p className="font-semibold text-gray-800">Locataire</p>
                    <p className="text-xs text-gray-500">Peut déclarer des incidents</p>
                  </button>
                </div>
              </div>

              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  required
                  disabled={!!editingAccount}
                />
                {editingAccount && (
                  <p className="text-xs text-gray-500 mt-1">L&apos;email ne peut pas être modifié</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.permissions.can_view_ag}
                      onChange={(e) => setFormData({
                        ...formData,
                        permissions: {...formData.permissions, can_view_ag: e.target.checked}
                      })}
                      className="w-4 h-4 text-[#00D3A8] rounded"
                    />
                    <span className="text-sm text-gray-700">Voir les Assemblées Générales</span>
                  </label>
                  {formData.type === 'delegue' && (
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.permissions.can_vote}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: {...formData.permissions, can_vote: e.target.checked}
                        })}
                        className="w-4 h-4 text-[#00D3A8] rounded"
                      />
                      <span className="text-sm text-gray-700">Voter aux AG (avec procuration)</span>
                    </label>
                  )}
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.permissions.can_view_documents}
                      onChange={(e) => setFormData({
                        ...formData,
                        permissions: {...formData.permissions, can_view_documents: e.target.checked}
                      })}
                      className="w-4 h-4 text-[#00D3A8] rounded"
                    />
                    <span className="text-sm text-gray-700">Consulter les documents</span>
                  </label>
                  {formData.type === 'locataire' && (
                    <>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.permissions.can_view_incidents}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: {...formData.permissions, can_view_incidents: e.target.checked}
                          })}
                          className="w-4 h-4 text-[#00D3A8] rounded"
                        />
                        <span className="text-sm text-gray-700">Voir les incidents</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.permissions.can_create_incidents}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: {...formData.permissions, can_create_incidents: e.target.checked}
                          })}
                          className="w-4 h-4 text-[#00D3A8] rounded"
                        />
                        <span className="text-sm text-gray-700">Déclarer des incidents</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Warning for delegate */}
              {formData.type === 'delegue' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-amber-700">
                    Pour que votre délégué puisse voter, vous devrez lui donner une procuration officielle lors de l&apos;AG.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-[#00D3A8] hover:bg-[#00C7A1] text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Check size={18} />
                      <span>{editingAccount ? 'Mettre à jour' : 'Créer le sous-compte'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SousComptes;
